"use client";

import * as Sentry from "@sentry/nextjs";
import { AlertTriangle, Home, RefreshCcw } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@defuse/ui/button";
import { logger } from "@intents-explorer/shared-api/logger";

export default function GlobalError({
	error,
	reset,
}: {
	error: Error & { digest?: string };
	reset: () => void;
}) {
	const [eventId, setEventId] = useState<string>();
	// Preserve the server log reference; client errors use their Sentry event ID.
	const supportId = error.digest ?? eventId;

	useEffect(() => {
		setEventId(Sentry.captureException(error));
		logger.error("Global error:", error);
	}, [error]);

	return (
		<html lang="en" suppressHydrationWarning={true}>
			<body className="antialiased">
				<div className="flex min-h-screen flex-col items-center justify-center p-4">
					<div className="bg-background mx-auto w-full max-w-md rounded-xl p-8 text-center shadow-lg">
						<div className="mb-6 flex justify-center">
							<div className="bg-brand-orange flex h-16 w-16 items-center justify-center rounded-full">
								<AlertTriangle className="h-8 w-8 text-white" />
							</div>
						</div>

						<h1 className="text-brand-orange mb-4 text-2xl font-bold">
							Something went wrong!
						</h1>

						<p className="text-muted-foreground mb-6 text-sm">
							An unexpected error occurred. We apologize for the inconvenience.
							Please try refreshing the page or return to the homepage.
						</p>

						{supportId !== undefined && (
							<div className="bg-muted/50 mb-6 rounded-lg p-3">
								<p className="text-muted-foreground text-xs">
									Send this ID to us:{" "}
									<code className="bg-muted rounded px-1 py-0.5 font-mono text-xs">
										{supportId}
									</code>
								</p>
							</div>
						)}

						<div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
							<Button
								onClick={reset}
								className="bg-brand-orange hover:bg-brand-orange/90 flex items-center gap-2 text-white"
							>
								<RefreshCcw className="h-4 w-4" />
								Try again
							</Button>
							<Button
								variant="outline"
								onClick={() => {
									window.location.href = "/";
								}}
								className="flex items-center gap-2"
							>
								<Home className="h-4 w-4" />
								Go home
							</Button>
						</div>
					</div>
				</div>
			</body>
		</html>
	);
}
